// El foco adentro del modal (BA-14, 2026-09-06).
//
// ═══════════════════════════════════════════════════════════════════
// QUÉ PASABA
// ═══════════════════════════════════════════════════════════════════
//
// Los seis modales de la app —perfil, ayuda, ficha de prenda, editor
// del armario, mis listas, cambiar prenda— se abrían y el foco del
// teclado se quedaba donde estaba: en el botón de atrás, en la página
// que ahora está tapada.
//
// Con el mouse no se nota nada. Con el teclado, el modal es una pared:
// se abre, y la primera vez que apretás Tab empezás a recorrer los
// controles de la página de ATRÁS, uno por uno, sin verlos —están
// cubiertos por el modal—, hasta salirte de la ventana. Para alguien
// que usa lector de pantalla es peor: le anuncian una página que ya no
// está mirando.
//
// Escape ya cerraba en los seis. Lo que faltaba era esto.
//
// ═══════════════════════════════════════════════════════════════════
// TRES COSAS, Y LA TERCERA ES LA QUE SE OLVIDA
// ═══════════════════════════════════════════════════════════════════
//
//   1. Al abrir, el foco ENTRA. Al primer control del modal, o a la
//      caja misma si no tiene ninguno.
//   2. Mientras está abierto, Tab da la vuelta adentro y no se escapa.
//   3. Al cerrar, el foco VUELVE a donde estaba. Sin esto, cerrás el
//      modal y el foco queda en la nada: el próximo Tab arranca desde
//      el principio del documento, y quien venía navegando pierde el
//      lugar en el que estaba.
//
// Va en un hook y no copiado en cada modal por la razón de siempre en
// este proyecto: un modal nuevo tendría que acordarse, y no acordarse
// no da error — da una pared que solo nota quien usa el teclado.

import { useEffect, useRef } from "react";

/* Lo que el navegador considera alcanzable con Tab.
 *
 * `[tabindex]:not([tabindex="-1"])` incluye los elementos que este
 * mismo proyecto hizo enfocables a mano: las fotos con role="button"
 * de las seis pantallas. */
const ENFOCABLES = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function enfocablesDe(caja) {
  if (!caja) return [];
  return [...caja.querySelectorAll(ENFOCABLES)].filter((el) => {
    /* `offsetParent === null` descarta lo que está oculto por CSS. Un
     * control invisible que igual recibe el foco es exactamente el
     * mismo problema que este archivo arregla, en chiquito. */
    if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") {
      return false;
    }
    return el.getAttribute("aria-hidden") !== "true";
  });
}

/**
 * Atrapa el foco adentro de un contenedor mientras `activo` sea true.
 *
 * @param {boolean} activo  si el modal está abierto. Se pasa como
 *   parámetro —en vez de montar/desmontar el hook— porque varios de
 *   estos modales viven inline dentro de un componente que NO se
 *   desmonta cuando se cierran.
 * @returns {object} el ref para poner en la caja del modal.
 */
export function useFocoAtrapado(activo = true) {
  const caja = useRef(null);

  useEffect(() => {
    if (!activo) return undefined;
    const nodo = caja.current;
    if (!nodo) return undefined;

    /* Quién tenía el foco antes. Se guarda en una variable del efecto y
     * no en un ref: si se guardara en un ref, una segunda apertura antes
     * de que la primera termine de cerrar pisaría el valor y el foco
     * volvería al lugar equivocado. */
    const antes = document.activeElement;

    const primeros = enfocablesDe(nodo);
    if (primeros.length) {
      primeros[0].focus();
    } else {
      /* Sin ningún control adentro, el foco va a la caja. Necesita
         tabindex para poder recibirlo, y -1 para no agregar una parada
         de tabulación que no debería existir. */
      nodo.setAttribute("tabindex", "-1");
      nodo.focus();
    }

    function alTabular(e) {
      if (e.key !== "Tab") return;
      const lista = enfocablesDe(nodo);
      if (!lista.length) {
        // Nada que enfocar adentro: Tab no hace nada, en vez de irse.
        e.preventDefault();
        return;
      }
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];

      /* Si el foco se fue afuera —pasa cuando el navegador restaura el
       * foco solo, o cuando un control desaparece bajo el foco— se lo
       * trae de vuelta en vez de dejar que siga por la página tapada. */
      if (!nodo.contains(document.activeElement)) {
        e.preventDefault();
        primero.focus();
        return;
      }

      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    }

    document.addEventListener("keydown", alTabular, true);

    return () => {
      document.removeEventListener("keydown", alTabular, true);
      /* Devolver el foco. `isConnected` porque el elemento de antes
       * puede haberse ido del documento mientras el modal estaba
       * abierto —una lista que se recargó—, y enfocar un nodo
       * desconectado no hace nada y encima pierde el foco de verdad. */
      if (antes && antes.isConnected && typeof antes.focus === "function") {
        antes.focus();
      }
    };
  }, [activo]);

  return caja;
}
